import GraphSrcSetImage from "@/Components/Front/Blocks/GraphSrcSetImage/GraphSrcSetImage";
import FrontText from "@/Components/Front/FrontText";
import GraphImage from "@/Components/Front/GraphImage/GraphImage";
import Column from "@/Components/Grid/Column";
import Container from "@/Components/Grid/Container";
import Row from "@/Components/Grid/Row";
import HeroSkeleton from "@/Components/Preloaders/HeroSkeleton";
import LargeSkeleton from "@/Components/Preloaders/LargeSkeleton";
import resolveLocaleRoute from "@/Helpers/resolveLocaleRoute";
import resolveTaxonomy from "@/Helpers/resolveTaxonomy";
import FrontLayout from "@/Layouts/System/FrontLayout";
import POSTS_LOOP_FRAGMENT from "@/Query/Fragments/POSTS_LOOP_FRAGMENT";
import { Icon } from "@iconify/react";
import { Head, Link } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

// import * as ApolloPkg from '@apollo/client';
// import { gql } from '@apollo/client/core/core.cjs';
// import { useQuery } from "@apollo/client/react/hooks/hooks.cjs";
// import { useQuery, gql } from "@apollo/client/apollo-client.min.cjs";
// import { useQuery, gql } from "@apollo/client";

import gql from 'graphql-tag';
import { useQuery } from "@apollo/client/react/hooks/hooks.cjs";
import FrontSeoMetaTags from "@/Components/Front/Partials/FrontSeoMetaTags";

const NewsLoop = ({ taxonomy, seo }) => {
    // const { gql, useQuery } = ApolloPkg;

    const PAGE_SIZE = 12;
    const { t, i18n } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);

    const currentLanguage = i18n.resolvedLanguage.toUpperCase()

    const LATEST_POSTS = gql`
        query GetPosts($categoryName: String!, $offset: Int, $size: Int, $lang: LanguageCodeFilterEnum!) {
            posts(where: { categoryName: $categoryName, language: $lang, status: PUBLISH, offsetPagination: { size: $size, offset: $offset } } ) {
                edges {
                    node {
                        ${POSTS_LOOP_FRAGMENT}
                    }
                    cursor
                }
                pageInfo {
                    offsetPagination{
                        total
                        hasMore
                        hasPrevious
                    }
                }
            }
        }
    `;

    const { loading, error, data, fetchMore } = useQuery(LATEST_POSTS, {
        variables: { categoryName: taxonomy, offset: (currentPage - 1) * PAGE_SIZE, size: PAGE_SIZE, lang: currentLanguage },
        ssr: true,
    });

    const updateQuery = (previousResult, { fetchMoreResult }) => {
        return fetchMoreResult.posts.edges.length ? fetchMoreResult : previousResult;
    };

    const Pagination = ({ pageInfo }) => {

        useEffect(() => {
            const params = new URLSearchParams(window.location.search);
            const pageParam = parseInt(params.get('page'));
            const page = isNaN(pageParam) || pageParam <= 0 ? 1 : pageParam;
            setCurrentPage(page);
        }, [currentPage, data, fetchMore]);

        const { total, hasMore, hasPrevious } = pageInfo.offsetPagination
        const totalPages = Math.ceil(total / PAGE_SIZE)
        const pagesToShow = 2;

        const calculateStartAndEnd = (currentPage, totalPages, pagesToShow) => {
            const halfPagesToShow = Math.floor(pagesToShow / 2);

            let start = currentPage - halfPagesToShow;
            let end = currentPage + halfPagesToShow;

            if (start < 1) {
                start = 1;
                end = Math.min(pagesToShow, totalPages);
            }

            if (end > totalPages) {
                end = totalPages;
                start = Math.max(1, totalPages - pagesToShow + 1);
            }

            return [start, end];
        };

        const [start, end] = calculateStartAndEnd(currentPage, totalPages, pagesToShow);

        const calculateOffset = (page) => {
            return (page - 1) * PAGE_SIZE;
        }

        const scrollToTop = () => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }

        const handlePrevPage = () => {
            setLoadingMore(true);

            fetchMore({
                variables: {
                    offset: calculateOffset(currentPage - 1),
                },
                updateQuery,
            }).then(() => {
                setLoadingMore(false)
                setCurrentPage(currentPage - 1);
            });

            scrollToTop()
            updateQueryParam(currentPage - 1);
        };

        const handleNextPage = () => {
            setLoadingMore(true);

            fetchMore({
                variables: {
                    offset: calculateOffset(currentPage + 1),
                },
                updateQuery,
            }).then(() => {
                setLoadingMore(false)
                setCurrentPage(currentPage + 1);
            });

            scrollToTop()
            updateQueryParam(currentPage + 1);

        };

        const handlePageChange = (page) => {
            setLoadingMore(true);

            fetchMore({
                variables: {
                    offset: calculateOffset(page),
                },
                updateQuery,
            }).then(() => {
                setLoadingMore(false)
                setCurrentPage(page);
            });

            scrollToTop()
            updateQueryParam(page);

        };

        const updateQueryParam = (page) => {
            const params = new URLSearchParams(window.location.search);
            params.set('page', page);

            if (page === 1) {
                window.history.pushState({}, '', window.location.pathname);
                return;
            }

            const newUrl = `${window.location.pathname}?${params.toString()}`;
            window.history.pushState({}, '', newUrl);
        };

        return (
            <div className="flex justify-center mt-13 mb-[130px]">
                {hasPrevious && (
                    <button onClick={handlePrevPage} className="mr-3 flex items-center text-black hover:text-light-blue hover:opacity-80 transition-all ease-in-out duration-200" title={t('alts.previous')}>
                        <Icon icon='formkit:arrowleft' width={20} className="ml-2" />
                        <FrontText textSize='16/20'>
                            {t('alts.previous')}
                        </FrontText>
                    </button>
                )}

                {start > 1 && (
                    <>
                        <button onClick={() => handlePageChange(1)} className="mx-3 border-[1px] border-dark-blue rounded-full w-11 h-11 flex items-center justify-center hover:bg-dark-blue-standard hover:text-white transition-all ease-in-out duration-300">
                            <span>1</span>
                        </button>
                        {start > 2 && (
                            <div className="mx-3 border-[1px] border-dark-blue-standard rounded-full w-11 h-11 flex items-center justify-center">
                                <span className="text-18 font-bold text-dark-blue">...</span>
                            </div>
                        )}
                    </>
                )}

                {Array.from({ length: end - start + 1 }).map((_, index) => {
                    const pageNumber = start + index;
                    return (
                        <button
                            key={index}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`mx-3 border-[1px] border-dark-blue-standard rounded-full w-11 h-11 flex items-center justify-center ${pageNumber === currentPage ? 'bg-dark-blue-standard text-white' : 'bg-white hover:bg-dark-blue-standard hover:text-white transition-all ease-in-out duration-300'
                                }`}
                        >
                            <span>{pageNumber}</span>
                        </button>
                    );
                })}

                {end < totalPages && (
                    <>
                        {end < totalPages - 1 && (
                            <div className="mx-3 border-[1px] border-dark-blue-standard rounded-full w-11 h-11 flex items-center justify-center">
                                <span className="text-18 font-bold text-dark-blue">...</span>
                            </div>
                        )}
                        <button onClick={() => handlePageChange(totalPages)} className="mx-3 border-[1px] border-dark-blue-standard rounded-full w-11 h-11 flex items-center justify-center hover:bg-dark-blue-standard hover:text-white transition-all ease-in-out duration-300">
                            <span>{totalPages}</span>
                        </button>
                    </>
                )}
                {hasMore && (
                    <button onClick={handleNextPage} className="ml-3 flex items-center text-black hover:text-light-blue hover:opacity-80 transition-all ease-in-out duration-200" title={t('alts.next')}>
                        <FrontText textSize='16/20'>
                            {t('alts.next')}
                        </FrontText>
                        <Icon icon='formkit:arrowright' width={20} className="ml-2" />
                    </button>
                )}
            </div>
        );
    };

    return (
        <FrontLayout breadcrumbs={[
            { title: t('breadcrumbs.news').toLowerCase(), href: resolveLocaleRoute('front.news', null, i18n.resolvedLanguage) },
            { title: resolveTaxonomy(taxonomy).toLocaleLowerCase(), current: true }
        ]}>
            <FrontSeoMetaTags seo={seo}/>
            <Container>
                <Row>
                    <Column>
                        <FrontText className="my-10 text-center font-bold font-domine" textSize={'38/43'}>
                            <h1>{resolveTaxonomy(taxonomy)}</h1>
                        </FrontText>
                    </Column>
                </Row>
            </Container>
            <div className="bg-new-blue pt-15 mb-40">
                <Container>
                    <Row>
                        <Column>
                            {error &&
                                <HeroSkeleton />
                            }
                            {(loading || loadingMore) ?
                                <HeroSkeleton />
                                :
                                <>
                                    {data && data.posts.edges.length === 0 &&
                                        <FrontText textSize='24/29' className="content font-bold font-domine text-white text-center transition-all ease-in-out duration-300 mb-20 uppercase">
                                            <h2>
                                                {t('general.noResults')}
                                            </h2>
                                        </FrontText>
                                    }
                                    {data && data.posts.edges.length > 0 && data.posts.edges.slice(0, 1).map((elem, index) => {
                                        const { id, slug, title, featuredImage, date, excerpt } = elem.node
                                        const { sizes } = featuredImage.node.mediaDetails
                                        const { srcSet } = featuredImage.node
                                        const { mediaItemUrl } = featuredImage.node
                                        const resolvedRoute = currentLanguage === 'PL' ? route('front.news.single', {taxonomy: taxonomy, slug: slug }) : route('front.news.single.en', {taxonomy: taxonomy, slug: slug })
                                        return (
                                            <Link key={id + index} href={resolvedRoute} title={title} className="block rounded-20 -mb-20 hover:bg-dark-blue-standard [&:hover_.content]:text-white bg-white transition-all ease-in-out duration-300 drop-shadow-hero-post">
                                                <Row>
                                                    <Column xl={6} lg={6}>
                                                        {sizes || !srcSet
                                                        ?
                                                            (
                                                                <GraphImage sizes={sizes} original={mediaItemUrl} className="w-full rounded-tl-20 lg:rounded-tr-none rounded-tr-20 lg:rounded-bl-20 h-full" alt={title} />
                                                            ) 
                                                        :
                                                            srcSet && 
                                                                (
                                                                    <GraphSrcSetImage srcSet={srcSet} className="w-full rounded-tl-20 lg:rounded-tr-none rounded-tr-20 lg:rounded-bl-20 h-full" alt={title}/>
                                                                )
                                                        }
                                                    </Column>
                                                    <Column xl={6} lg={6}>
                                                        <article className="transition-all ease-in-out duration-300 lg:pr-10 pr-4 pl-4 h-full">
                                                            <div className="lg:py-9 py-5 h-full flex flex-col lg:px-0 px-2">
                                                                <FrontText textSize='24/29' className="content font-bold font-domine text-standard-black transition-all ease-in-out duration-300 mb-5 uppercase">
                                                                    <h2>
                                                                        {title}
                                                                    </h2>
                                                                </FrontText>

                                                                <FrontText textSize='18/22' className="content font-domine text-standard-black transition-all ease-in-out duration-300 mb-6">
                                                                    <div dangerouslySetInnerHTML={{ __html: excerpt }} />
                                                                </FrontText>

                                                                <div className="flex items-center justify-between text-30548A mt-auto">
                                                                    <FrontText textSize='20/24' className="font-bold font-domine">
                                                                        <span>
                                                                            {new Date(date).toLocaleDateString(currentLanguage, { day: 'numeric', month: 'long', year: 'numeric' })}
                                                                        </span>
                                                                    </FrontText>

                                                                    <Icon icon='material-symbols:menu-book-rounded' width={46} height={32} />
                                                                </div>
                                                            </div>
                                                        </article>
                                                    </Column>
                                                </Row>
                                            </Link>
                                        )
                                    })}
                                </>
                            }
                        </Column>
                    </Row>
                </Container>
            </div>
            <section>
                <Container>
                    <Row>
                        {(loading || loadingMore) &&
                            <Column>
                                <LargeSkeleton />
                                <LargeSkeleton />
                            </Column>
                        }
                        <Column className='flex flex-wrap sm:flex-row flex-col lg:gap-0 sm:gap-x-6'>
                            {data && data.posts.edges.slice(1).map((elem, index) => {
                                const { id, slug, title, featuredImage, date, excerpt } = elem.node
                                const { sizes } = featuredImage.node.mediaDetails
                                const { srcSet } = featuredImage.node
                                const { mediaItemUrl } = featuredImage.node

                                return (
                                    <Link key={id + index} href={route(currentLanguage === 'PL' ? 'front.news.single' : 'front.news.single.en', {taxonomy: taxonomy, slug: slug })} title={title} className="block rounded-10 mb-10 hover:bg-dark-blue-standard [&:hover_.content]:text-white bg-white transition-all ease-in-out duration-300 border-[1px] border-ECECEC lg:w-full sm:w-[calc(50%_-_0.75rem)]">
                                        <Row className=' items-stretch lg:items-start h-full flex-flow-nowrap'>
                                            <Column xl={4} lg={5} className='lg:h-full h-auto'>
                                                {sizes || !srcSet
                                                ?
                                                    (
                                                        <GraphImage sizes={sizes} original={mediaItemUrl} className="w-full rounded-tl-10 lg:rounded-bl-10 rounded-tr-10 lg:rounded-tr-none h-full" alt={title} />
                                                    ) 
                                                :
                                                    srcSet && 
                                                        (
                                                            <GraphSrcSetImage srcSet={srcSet} className="w-full rounded-tl-10 lg:rounded-bl-10 rounded-tr-10 lg:rounded-tr-none h-full" alt={title}/>
                                                        )
                                                }
                                            </Column>
                                            <Column xl={8} lg={7} className='h-full lg:flex-initial flex-auto'>
                                                <article className="transition-all ease-in-out duration-300 lg:pr-10 pr-4 pl-4 h-full">
                                                    <div className="py-5 h-full flex flex-col">
                                                        <FrontText textSize='20/23' className="content font-bold font-domine text-standard-black transition-all ease-in-out duration-300 mb-5 uppercase">
                                                            <h3>
                                                                {title}
                                                            </h3>
                                                        </FrontText>

                                                        <FrontText textSize='15/17' className="content font-domine text-standard-black transition-all ease-in-out duration-300 mb-4">
                                                            <div dangerouslySetInnerHTML={{ __html: excerpt }} />
                                                        </FrontText>

                                                        <div className="flex items-center justify-between text-30548A mt-auto">
                                                            <FrontText textSize='14/16' className="font-bold font-domine">
                                                                <span>
                                                                    {new Date(date).toLocaleDateString(currentLanguage, { day: 'numeric', month: 'long', year: 'numeric' })}
                                                                </span>
                                                            </FrontText>

                                                            <Icon icon='material-symbols:menu-book-rounded' width={24} />
                                                        </div>
                                                    </div>
                                                </article>
                                            </Column>
                                        </Row>
                                    </Link>
                                )
                            })}
                        </Column>
                        <Column>
                            {data && data.posts.edges.length > 0 && <Pagination pageInfo={data.posts.pageInfo} />}
                        </Column>
                    </Row>
                </Container>
            </section>
        </FrontLayout>
    );
};

export default NewsLoop;